import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { setScrollLocked } from "@/lib/smoothScroll";

const iconBtn = "flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 bg-ink/70 text-gold backdrop-blur-md transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink";

export const Lightbox = ({ images, index, setIndex, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const open = index !== null;
  const img = open ? images[index] : null;
  const go = (d) => { setZoomed(false); setIndex((index + d + images.length) % images.length); };

  useEffect(() => {
    if (!open) return undefined;
    setScrollLocked(true);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); setScrollLocked(false); };
  });

  useEffect(() => { setZoomed(false); }, [open]);

  const track = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div key="lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[60] flex flex-col bg-ink/95 backdrop-blur-xl" data-testid="photo-lightbox" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between px-5 py-4 sm:px-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone2" data-testid="lightbox-caption">
              {img.label} · {index + 1}/{images.length}
            </p>
            <div className="flex gap-3">
              <button className={iconBtn} onClick={() => setZoomed((z) => !z)} data-testid="lightbox-zoom-toggle" aria-label={zoomed ? "Zoom out" : "Zoom in"}>
                {zoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
              </button>
              <button className={iconBtn} onClick={onClose} data-testid="lightbox-close" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-6 sm:px-24">
            <div className="relative h-full max-h-[calc(100svh-7rem)] overflow-hidden" onPointerMove={track} onClick={(e) => { track(e); setZoomed((z) => !z); }}>
              <motion.img key={img.src} src={img.src} alt={img.alt} data-testid="lightbox-image"
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: zoomed ? 2.5 : 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} style={{ transformOrigin: origin, touchAction: zoomed ? "none" : "auto" }}
                className={`h-full max-w-full object-contain ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`} />
            </div>
            {images.length > 1 && (
              <>
                <button className={`${iconBtn} absolute left-3 top-1/2 -translate-y-1/2 sm:left-8`} onClick={() => go(-1)} data-testid="lightbox-prev" aria-label="Previous photo"><ChevronLeft className="h-5 w-5" /></button>
                <button className={`${iconBtn} absolute right-3 top-1/2 -translate-y-1/2 sm:right-8`} onClick={() => go(1)} data-testid="lightbox-next" aria-label="Next photo"><ChevronRight className="h-5 w-5" /></button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
